<template>
  <div class="flex flex-col gap-8">
    <div class="flex flex-row items-center justify-between">
      <Label for="logo">Workspace icon</Label>
      <div
        v-if="workspace.icon_url"
        class="flex flex-row gap-3 items-center relative"
      >
        <Avatar
          class="cursor-pointer"
          :src="workspace.icon_url"
          size="large"
          @click="cropperDialogOpen = true"
        />
        <IconButton
          variant="danger"
          size="sm"
          class="p-0 absolute -top-2 -right-2 size-4 rounded-full"
          @click="onIconDelete"
        >
          <X class="size-2" />
        </IconButton>
      </div>
      <Button
        v-else
        variant="outline"
        class="rounded-full border-dashed border-neutral-400 size-12"
        @click="cropperDialogOpen = true"
      >
        <template #startIcon><Plus /></template>
      </Button>
      <ImageCropperDialog v-model:open="cropperDialogOpen" />
    </div>
    <div class="flex flex-col gap-3">
      <Label for="logo">Workspace ID</Label>
      <div class="flex gap-2 items-center">
        <span class="text-neutral-400">{{ workspace.hash }}</span>
        <button type="button" class="text-neutral-500" @click="copy()">
          <ClipboardList v-if="!copied" class="size-5" />
          <ClipboardCheck v-else class="size-5" />
        </button>
      </div>
    </div>
    <form
      id="workspace-settings"
      class="flex flex-col gap-8"
      @submit="onSubmit"
    >
      <fieldset class="flex flex-col gap-1">
        <Label for="name">Workspace name</Label>
        <Input v-bind="nameAttrs" id="name" v-model="name" />
        <span class="text-error-400">{{ errors?.name }}</span>
      </fieldset>

      <fieldset class="flex flex-col gap-1">
        <Label for="billingEmail">Billing email</Label>
        <span class="text-neutral-400">
          Where the workspace’s invoices will be send to
        </span>
        <Input
          v-bind="billingEmailAttrs"
          id="billingEmail"
          v-model="billingEmail"
          type="email"
        />
        <span class="text-error-400">{{ errors?.billingEmail }}</span>
      </fieldset>
    </form>
    <div>
      <PlanSection :workspace="workspace" />
    </div>
    <div>
      <DangerSection :workspace="workspace" />
    </div>
  </div>
  <StickyBottomCTA
    :is-pending="isPending"
    :meta="meta"
    :reset-form="resetForm"
    form-id="workspace-settings"
  />
</template>

<script setup>
import { ref, watch } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { toast } from 'vue-sonner';
import { Plus, X, ClipboardList, ClipboardCheck } from 'lucide-vue-next';
import { useClipboard } from '@vueuse/core';

import requests from '@vuex/requests.js';

import { Input } from '@ui/components/input';
import { Label } from '@ui/components/label';

import StickyBottomCTA from '@components/common/StickyBottomCTA.vue';
import Avatar from '@/ui/components/avatar/Avatar.vue';
import { IconButton } from '@ui/components/icon-button';
import { Button } from '@ui/components/button';
import { queries } from '@/queries/index.js';
import { useStore } from 'vuex';
import PlanSection from './settings/PlanSection.vue';
import DangerSection from './settings/DangerSection.vue';
import ImageCropperDialog from './settings/ImageCropperDialog.vue';

const cropperDialogOpen = ref(false);
const queryClient = useQueryClient();

const props = defineProps({
  workspace: {
    type: Object,
    required: true,
  },
});

const { copy, copied } = useClipboard({
  source: props.workspace.hash,
});

const validationSchema = toTypedSchema(
  z.object({
    name: z
      .string()
      .min(1, {
        message: 'The name field is required',
      })
      .max(64),
    billingEmail: z
      .string()
      .min(1, {
        message: 'The email field is required',
      })
      .email('This is not a valid email.')
      .max(255),
  })
);

const { handleSubmit, errors, defineField, meta, resetField, resetForm } =
  useForm({
    validationSchema,
    initialValues: {
      name: props.workspace?.name,
      billingEmail: props.workspace?.billing_email,
    },
  });

const [name, nameAttrs] = defineField('name');
const [billingEmail, billingEmailAttrs] = defineField('billingEmail');

watch(
  () => props.workspace,
  newValue => {
    resetField('name', {
      value: newValue.name,
      touched: false,
      errors: [],
    });
  }
);
const store = useStore();
const { isPending, mutate } = useMutation({
  mutationFn: async payload => {
    await requests.workspaces.update(store.getters['workspace/getId'], {
      name: payload.name,
      billing_email: payload.billingEmail,
    });
  },
  onSuccess: async () => {
    toast.success('Workspace settings has been updated!');
    await queryClient.invalidateQueries();
  },
});

const onSubmit = handleSubmit((values, { resetForm: reset }) => {
  mutate(values);

  reset({
    values,
  });
});

function onIconDelete() {
  requests.workspaces
    .changeIcon(store.getters['workspace/getId'], { image: null })
    .then(() => {
      queryClient.invalidateQueries({
        queryKey: queries.workspace.current.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queries.workspace.connections.queryKey,
      });
      cropperDialogOpen.value = false;
    });
}
</script>
